import type { ReactNode } from 'react';
import { cx, TONE_ICON, TONE_WORD, type BannerTone } from '../internal/tone';
import { IconButton } from '../actions/IconButton';

export interface BannerProps {
  tone: BannerTone;
  title: string;
  children?: ReactNode;
  onDismiss?: () => void;
  icon?: string;
  className?: string;
}

/**
 * Aviso permanente na tela (ao contrário do toast, que desaparece).
 *
 * A "sobrancelha" com a palavra do tom — ERRO, ATENÇÃO, TUDO CERTO, INFORMAÇÃO —
 * é visível de propósito: é ela que faz o significado não depender da cor.
 * Erros e avisos são anunciados com `role="alert"`; sucesso e informação com
 * `role="status"`, para não interromper o que ela está fazendo.
 */
export function Banner({ tone, title, children, onDismiss, icon, className }: BannerProps) {
  const urgent = tone === 'danger' || tone === 'warning';

  return (
    <div
      className={cx('flos-banner', `flos-banner--${tone}`, className)}
      role={urgent ? 'alert' : 'status'}
    >
      <span className="flos-banner__icon flos-icon" aria-hidden="true">
        {icon ?? TONE_ICON[tone]}
      </span>
      <div className="flos-banner__text">
        <span className="flos-banner__eyebrow">{TONE_WORD[tone]}</span>
        <h3 className="flos-banner__title">{title}</h3>
        {children ? <div>{children}</div> : null}
      </div>
      {onDismiss ? <IconButton icon="✕" label="Fechar aviso" onClick={onDismiss} /> : null}
    </div>
  );
}
